import React from "react";
import { X, Swords } from "lucide-react";
import type { MypageParticipatedMatchResult } from "../../types/swagger";

interface MatchHistoryDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventName: string;
  eventDate: string;
  singlesRank: number;
  singlesPoint: number;
  matchResults: MypageParticipatedMatchResult[];
}

const formatEventDate = (isoDateString: string): string => {
  if (!isoDateString) return "";
  try {
    const d = new Date(isoDateString);
    if (Number.isNaN(d.getTime())) return isoDateString;
    const jst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
    const yyyy = jst.getUTCFullYear();
    const mm = String(jst.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(jst.getUTCDate()).padStart(2, "0");
    return `${yyyy} / ${mm} / ${dd}`;
  } catch {
    return isoDateString;
  }
};

const formatRank = (rank: number): string => {
  if (rank === 1) return "優勝";
  if (rank === 2) return "準優勝";
  if (rank === 4) return "ベスト4";
  if (rank === 8) return "ベスト8";
  if (rank === 16) return "ベスト16";
  if (rank === 32) return "ベスト32";
  return `${rank}位`;
};

/** rank (自分の順位) からラウンド名を返す */
const formatMatchRound = (rank: number): string => {
  if (rank <= 2) return "決勝";
  if (rank <= 4) return "準決勝";
  if (rank <= 8) return "準々決勝";
  if (rank <= 16) return "ベスト16";
  if (rank <= 32) return "ベスト32";
  return `ベスト${rank}`;
};

const MatchHistoryDetailModal: React.FC<MatchHistoryDetailModalProps> = ({
  isOpen,
  onClose,
  eventName,
  eventDate,
  singlesRank,
  singlesPoint,
  matchResults,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="bg-white w-full rounded-t-[22px] shadow-2xl animate-in slide-in-from-bottom duration-300 max-h-[85vh] overflow-y-auto scrollbar-hide"
        onClick={(e) => e.stopPropagation()}
      >
        {/* drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-slate-200 rounded-full" />
        </div>

        {/* header */}
        <div className="flex items-start justify-between px-6 pt-3 pb-4">
          <div className="flex-1 min-w-0 pr-4">
            <p className="font-black text-[15px] text-slate-800 leading-snug">
              {eventName}
            </p>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
              {formatEventDate(eventDate)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 自分の成績サマリー */}
        <div className="mx-6 mb-5 flex gap-3">
          <div className="flex-1 bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
            <p className="text-[9px] font-bold text-emerald-500 uppercase tracking-widest mb-1">
              順位
            </p>
            <p
              className={`font-black text-base ${singlesRank === 1 ? "text-amber-500" : "text-slate-700"}`}
            >
              {formatRank(singlesRank)}
            </p>
          </div>
          <div className="flex-1 bg-emerald-50 rounded-xl p-3 text-center border border-emerald-100">
            <p className="text-[9px] font-bold text-emerald-500 uppercase tracking-widest mb-1">
              獲得ポイント
            </p>
            <p className="font-black text-base text-emerald-600">
              +{singlesPoint}pt
            </p>
          </div>
        </div>

        {/* 対戦結果リスト */}
        <div className="flex items-center gap-1.5 px-6 pb-3">
          <Swords className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
            対戦結果
          </span>
        </div>

        <div className="px-6 pb-8">
          {matchResults.length === 0 ? (
            <div className="bg-slate-50 rounded-[14px] p-4 text-center text-slate-400 text-xs font-bold">
              対戦データがありません
            </div>
          ) : (
            <div className="space-y-2">
              {matchResults.map((result, i) => {
                const iWon = result.my_win_count > result.opponent_win_count;
                const isDraw =
                  result.my_win_count === result.opponent_win_count;
                return (
                  <div
                    key={i}
                    className={`rounded-[14px] p-4 border flex flex-col gap-2 ${
                      iWon
                        ? "bg-emerald-50 border-emerald-200"
                        : isDraw
                          ? "bg-slate-50 border-slate-200"
                          : "bg-rose-50 border-rose-200"
                    }`}
                  >
                    {/* ラウンドラベル */}
                    <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                      {formatMatchRound(result.rank)}
                    </span>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col min-w-0">
                        <span className="font-bold text-slate-800 text-sm truncate">
                          {result.opponent_dartslive_card_name}
                        </span>
                        <span className="text-[10px] text-slate-400 font-medium">
                          {result.opponent_login_id}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="text-right">
                          <p
                            className={`font-black text-lg leading-none ${
                              iWon
                                ? "text-emerald-600"
                                : isDraw
                                  ? "text-slate-500"
                                  : "text-rose-500"
                            }`}
                          >
                            {result.my_win_count} — {result.opponent_win_count}
                          </p>
                        </div>
                        <span
                          className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${
                            iWon
                              ? "bg-emerald-200 text-emerald-700"
                              : isDraw
                                ? "bg-slate-200 text-slate-600"
                                : "bg-rose-200 text-rose-700"
                          }`}
                        >
                          {iWon ? "WIN" : isDraw ? "DRAW" : "LOSE"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchHistoryDetailModal;
